// ── recovery 세션 AMR 판별 공용 모듈 ──────────────────────────────────────
// AuthProvider 의 복구 세션 판별 로직을 순수 함수로 분리했다.
// 복구 권한은 URL 의 type=recovery 문자열이 아니라, 서명 검증된 세션 JWT 의
// amr.method='recovery' claim(만료 시간 검사)으로 판단한다. 검증 실패는 fail-closed(false).

export const RECOVERY_WINDOW_MS = 60 * 60 * 1000;

export interface AmrClaim {
  method?: unknown;
  timestamp?: unknown;
}

export interface AuthClaims {
  amr?: unknown;
  exp?: unknown;
}

/**
 * getClaims() 가 서명 검증한 claims 에서 recovery amr(만료 시간 검사)가 있는지 확인한다.
 * - amr 누락 / 배열 아님 / 구조 오류 → false
 * - exp(세션 만료)가 없거나 유한한 숫자가 아니면 → false (fail-closed)
 * - exp 가 지났으면 → false (만료된 세션은 recovery 로 인정하지 않음)
 * - 1시간 초과 / 미래 timestamp → false
 * - 초 단위/밀리초 단위 timestamp 정규화
 * - URL #type=recovery 문자열은 판정에 사용하지 않음
 */
export function hasRecentRecoveryAmr(
  claims: AuthClaims | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!claims || typeof claims !== 'object') return false;

  // 만료 검사는 fail-closed: exp 가 없거나 유한한 숫자가 아니면 recovery 로 인정하지 않는다.
  // (서명 검증을 거친 recovery 세션이라도 만료/잘못된 exp 는 false)
  const exp = (claims as AuthClaims).exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return false;
  }
  const expMs = exp > 1e12 ? exp : exp * 1000;
  if (expMs <= now) return false;

  const amr = (claims as AuthClaims).amr;
  if (!Array.isArray(amr)) return false;

  return amr.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const e = entry as AmrClaim;
    if (e.method !== 'recovery') return false;
    if (typeof e.timestamp !== 'number' || !Number.isFinite(e.timestamp)) return false;
    const ts = e.timestamp > 1e12 ? e.timestamp : e.timestamp * 1000;
    if (ts > now) return false; // 미래 timestamp → false
    return now - ts <= RECOVERY_WINDOW_MS;
  });
}